import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { createService, listServices, patchService, type CreateServiceInput, type PatchServiceInput } from '../api/endpoints/services';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import type { BusinessServiceRecord } from '../api/schemas';
import { RequirePlan } from '../auth/RequirePlan';
import { ConfirmAction } from '../components/ConfirmAction';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { formatMoneyCents } from '../lib/format';
import { modeForCategory } from '../lib/mode';
import { centsToDollarsInput, parsePriceInput } from '../lib/money';
import { useBusiness } from '../lib/useBusiness';
import { MenuPage } from './Menu';

const SERVICES_QUERY_KEY = ['business-services'];

/**
 * /servicios serves two different jobs: a plain, never-gated service catalog
 * for every business, or the restaurant Menu manager, which is Pro (the
 * catalog's `menu`; below Pro it shows its lock). The route itself stays
 * ungated (a salon or barbershop must always reach its catalog); only the
 * restaurant branch checks the plan.
 */
export function ServiciosPage() {
  const business = useBusiness();
  return business && modeForCategory(business.category) === 'restaurant' ? (
    <RequirePlan capability="menu">
      <MenuPage />
    </RequirePlan>
  ) : (
    <LegacyServiciosPage />
  );
}

/** Appointment catalog for non-restaurant businesses. */
function LegacyServiciosPage() {
  const queryClient = useQueryClient();
  const servicesQuery = useQuery({
    queryKey: SERVICES_QUERY_KEY,
    queryFn: () => listServices(true),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [durationMin, setDurationMin] = useState('');
  const [priceError, setPriceError] = useState<string | undefined>();

  const createMutation = useMutationAttempt({
    mutationFn: (input: CreateServiceInput, key: string) => createService(input, key),
    exito: 'Servicio agregado',
    onSuccess: () => {
      setName('');
      setDescription('');
      setPriceInput('');
      setDurationMin('');
      setPriceError(undefined);
      void queryClient.invalidateQueries({ queryKey: SERVICES_QUERY_KEY });
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (createMutation.uncertain) { createMutation.retry(); return; }
    const price = parsePriceInput(priceInput);
    if (!price.ok) {
      setPriceError(price.message);
      return;
    }
    setPriceError(undefined);
    createMutation.mutate({ name, ...(description.trim() ? { description: description.trim() } : {}), priceCents: price.cents, durationMin: Number(durationMin) });
  }

  const services = servicesQuery.data?.data ?? [];

  return (
    <main>
      <PageHeader
        title="Servicios"
        description="Lo que cobras: precio y duración de cada uno."
      />
      {servicesQuery.isLoading ? (
        <LoadingState label="Cargando servicios…" />
      ) : null}
      {servicesQuery.isError ? <ErrorBanner message={errorMessage(servicesQuery.error)} /> : null}
      {servicesQuery.data ? (
        services.length === 0 ? (
          <EmptyState
            title="Todavía no tienes servicios."
            description="Agrega lo que cobras: tus clientes lo verán al reservar."
          />
        ) : (
          <ul className="rows">
            {services.map((service) => (
              <li key={service.id}>
                <ServiceRow service={service} />
              </li>
            ))}
          </ul>
        )
      ) : null}

      <section className="section" aria-label="Agregar servicio">
        <h2 className="section-label">Agregar servicio</h2>
        <form className="card form-grid cols-2" onSubmit={handleSubmit}>
          <FormField label="Nombre" htmlFor="service-name" className="span-2">
            <input id="service-name" value={name} onChange={(event) => setName(event.target.value)} required />
          </FormField>
          <FormField label="Descripción" htmlFor="service-description" hint="Opcional" className="span-2">
            <input
              id="service-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </FormField>
          <FormField label="Precio" htmlFor="service-price" error={priceError} hint="En dólares, por ejemplo 12.50">
            <input
              id="service-price"
              inputMode="decimal"
              placeholder="12.50"
              value={priceInput}
              onChange={(event) => setPriceInput(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Duración (minutos)" htmlFor="service-duration">
            <input
              id="service-duration"
              type="number"
              min={1}
              max={480}
              value={durationMin}
              onChange={(event) => setDurationMin(event.target.value)}
              required
            />
          </FormField>
          {createMutation.isError ? <ErrorBanner message={errorMessage(createMutation.error)} /> : null}
          <button type={createMutation.uncertain ? "button" : "submit"} onClick={createMutation.uncertain ? createMutation.retry : undefined} className="btn" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Agregando…' : createMutation.uncertain ? 'Reintentar guardado' : 'Agregar servicio'}
          </button>
        </form>
      </section>
    </main>
  );
}

type ServiceRowProps = { service: BusinessServiceRecord };

function ServiceRow({ service }: ServiceRowProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(service.name);
  const [description, setDescription] = useState(service.description ?? '');
  const [priceInput, setPriceInput] = useState(centsToDollarsInput(service.priceCents));
  const [durationMin, setDurationMin] = useState(String(service.durationMin));
  const [priceError, setPriceError] = useState<string | undefined>();

  const editMutation = useMutationAttempt({
    mutationFn: (input: PatchServiceInput, key: string) => patchService(service.id, input, key),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: SERVICES_QUERY_KEY });
    },
  });

  const toggleMutation = useMutationAttempt({
    mutationFn: (isActive: boolean, key: string) => patchService(service.id, { isActive }, key),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SERVICES_QUERY_KEY }),
  });

  function handleEditSubmit(event: FormEvent) {
    event.preventDefault();
    if (editMutation.uncertain) { editMutation.retry(); return; }
    const price = parsePriceInput(priceInput);
    if (!price.ok) {
      setPriceError(price.message);
      return;
    }
    setPriceError(undefined);
    editMutation.mutate({ name, ...(description.trim() ? { description: description.trim() } : {}), priceCents: price.cents, durationMin: Number(durationMin) });
  }

  return (
    <div className="row">
      {editing ? (
        <form className="form-grid" onSubmit={handleEditSubmit}>
          <FormField label="Nombre" htmlFor={`service-name-${service.id}`}>
            <input
              id={`service-name-${service.id}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Descripción" htmlFor={`service-description-${service.id}`} hint="Opcional">
            <input
              id={`service-description-${service.id}`}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </FormField>
          <FormField
            label="Precio"
            htmlFor={`service-price-${service.id}`}
            error={priceError}
            hint="En dólares, por ejemplo 12.50"
          >
            <input
              id={`service-price-${service.id}`}
              inputMode="decimal"
              value={priceInput}
              onChange={(event) => setPriceInput(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Duración (minutos)" htmlFor={`service-duration-${service.id}`}>
            <input
              id={`service-duration-${service.id}`}
              type="number"
              min={1}
              max={480}
              value={durationMin}
              onChange={(event) => setDurationMin(event.target.value)}
              required
            />
          </FormField>
          <span className="action-pair">
            <button type={editMutation.uncertain ? "button" : "submit"} onClick={editMutation.uncertain ? editMutation.retry : undefined} className="btn small" disabled={editMutation.isPending}>
              {editMutation.uncertain ? 'Reintentar guardado' : 'Guardar'}
            </button>
            <button type="button" className="btn small ghost" disabled={editMutation.isPending || editMutation.uncertain} onClick={() => setEditing(false)}>
              Cancelar
            </button>
          </span>
        </form>
      ) : (
        <>
          <div className="grow">
            <p className="row-title">
              {service.name}{' '}
              {service.isActive ? null : (
                <span className="status" data-tone="off">
                  Desactivado
                </span>
              )}
            </p>
            <p className="row-sub">
              {service.description ? `${service.description} · ` : ''}
              {service.durationMin} min · {formatMoneyCents(service.priceCents)}
            </p>
          </div>
          <span className="action-pair">
            <button type="button" className="btn small ghost" disabled={toggleMutation.isPending || toggleMutation.uncertain} onClick={() => setEditing(true)}>
              Editar
            </button>
            <ConfirmAction
              label={toggleMutation.uncertain ? 'Reintentar cambio' : service.isActive ? 'Desactivar' : 'Activar'}
              pending={toggleMutation.isPending}
              onConfirm={() => toggleMutation.uncertain ? toggleMutation.retry() : toggleMutation.mutate(!service.isActive)}
            />
          </span>
        </>
      )}
      {editMutation.isError ? <ErrorBanner message={errorMessage(editMutation.error)} /> : null}
      {toggleMutation.isError ? <ErrorBanner message={errorMessage(toggleMutation.error)} /> : null}
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
